import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { sb } from "@/lib/sb";
import { useServerFn } from "@tanstack/react-start";
import { forwardFormSubmission } from "@/lib/forward-submission.functions";
import { Trash2, Plus, ArrowUp, ArrowDown, Forward } from "lucide-react";


export const Route = createFileRoute("/_authenticated/dashboard/form")({
  component: FormPage,
});

type FieldType = "text" | "email" | "tel" | "textarea" | "file" | "select";
type Field = {
  key: string;
  label: string;
  type: FieldType;
  placeholder?: string;
  required?: boolean;
  visible?: boolean;
  options?: string[];
};
type FormConfig = {
  title: string;
  submit_label: string;
  success_message: string;
  fields: Field[];
};

type Sub = {
  id: string;
  name: string | null;
  phone: string | null;
  email: string | null;
  project_type: string | null;
  message: string | null;
  status: "new" | "read" | "archived";
  created_at: string;
  metadata: Record<string, unknown> | null;
};

function FormPage() {
  const [tab, setTab] = useState<"form" | "inbox">("form");
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-display text-2xl font-bold text-ink">Form</h1>
        <div className="flex gap-2">
          <button
            onClick={() => setTab("form")}
            className={`px-4 py-2 rounded-lg text-sm font-medium ${
              tab === "form"
                ? "bg-brand text-primary-foreground"
                : "bg-card border border-border"
            }`}
          >
            Edit Form
          </button>
          <button
            onClick={() => setTab("inbox")}
            className={`px-4 py-2 rounded-lg text-sm font-medium ${
              tab === "inbox"
                ? "bg-brand text-primary-foreground"
                : "bg-card border border-border"
            }`}
          >
            Inbox
          </button>
        </div>
      </div>

      {tab === "form" ? <FormEditor /> : <Inbox />}
    </div>
  );
}

function FormEditor() {
  const [config, setConfig] = useState<FormConfig | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    sb.from("app_settings")
      .select("contact_form_config")
      .eq("id", 1)
      .maybeSingle()
      .then(({ data }: { data: { contact_form_config: FormConfig } | null }) => {
        if (data?.contact_form_config) setConfig(data.contact_form_config);
      });
  }, []);

  if (!config) return <p className="text-muted-foreground">Loading…</p>;

  const updateField = (i: number, patch: Partial<Field>) => {
    const fields = [...config.fields];
    fields[i] = { ...fields[i], ...patch };
    setConfig({ ...config, fields });
  };
  const removeField = (i: number) => {
    const fields = config.fields.filter((_, idx) => idx !== i);
    setConfig({ ...config, fields });
  };
  const move = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= config.fields.length) return;
    const fields = [...config.fields];
    [fields[i], fields[j]] = [fields[j], fields[i]];
    setConfig({ ...config, fields });
  };
  const addField = () => {
    const key = `custom_${Date.now()}`;
    setConfig({
      ...config,
      fields: [
        ...config.fields,
        { key, label: "New Field", type: "text", required: false, visible: true },
      ],
    });
  };

  const save = async () => {
    setSaving(true);
    const { error } = await sb
      .from("app_settings")
      .update({ contact_form_config: config })
      .eq("id", 1);
    setSaving(false);
    if (error) return toast.error(error.message);
    toast.success("Form saved");
  };

  return (
    <div className="space-y-6">
      <div className="bg-card border border-border rounded-xl p-5 space-y-4">
        <h2 className="font-display font-semibold text-ink">General</h2>
        <div className="grid gap-4 md:grid-cols-3">
          <Labeled label="Form Title">
            <input
              className="w-full px-3 py-2 rounded-lg bg-card border border-border text-sm"
              value={config.title}
              onChange={(e) => setConfig({ ...config, title: e.target.value })}
            />
          </Labeled>
          <Labeled label="Submit Button Text">
            <input
              className="w-full px-3 py-2 rounded-lg bg-card border border-border text-sm"
              value={config.submit_label}
              onChange={(e) => setConfig({ ...config, submit_label: e.target.value })}
            />
          </Labeled>
          <Labeled label="Success Message">
            <input
              className="w-full px-3 py-2 rounded-lg bg-card border border-border text-sm"
              value={config.success_message}
              onChange={(e) => setConfig({ ...config, success_message: e.target.value })}
            />
          </Labeled>
        </div>
      </div>

      <div className="bg-card border border-border rounded-xl p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-display font-semibold text-ink">Fields</h2>
          <button
            onClick={addField}
            className="flex items-center gap-1 text-sm bg-background border border-border rounded-lg px-3 py-1.5"
          >
            <Plus size={14} /> Add Field
          </button>
        </div>
        <div className="space-y-3">
          {config.fields.map((f, i) => (
            <div
              key={f.key}
              className="border border-border rounded-lg p-4 bg-background space-y-3"
            >
              <div className="flex items-center justify-between gap-3">
                <span className="font-mono text-xs text-muted-foreground">
                  {f.key}
                </span>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => move(i, -1)}
                    className="p-1 hover:bg-card rounded"
                    title="Move up"
                  >
                    <ArrowUp size={14} />
                  </button>
                  <button
                    onClick={() => move(i, 1)}
                    className="p-1 hover:bg-card rounded"
                    title="Move down"
                  >
                    <ArrowDown size={14} />
                  </button>
                  <button
                    onClick={() => removeField(i)}
                    className="p-1 hover:bg-card rounded text-destructive"
                    title="Delete"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
              <div className="grid gap-3 md:grid-cols-2">
                <Labeled label="Label">
                  <input
                    className="w-full px-3 py-2 rounded-lg bg-card border border-border text-sm"
                    value={f.label}
                    onChange={(e) => updateField(i, { label: e.target.value })}
                  />
                </Labeled>
                <Labeled label="Placeholder">
                  <input
                    className="w-full px-3 py-2 rounded-lg bg-card border border-border text-sm"
                    value={f.placeholder ?? ""}
                    onChange={(e) => updateField(i, { placeholder: e.target.value })}
                  />
                </Labeled>
                <Labeled label="Type">
                  <select
                    className="w-full px-3 py-2 rounded-lg bg-card border border-border text-sm"
                    value={f.type}
                    onChange={(e) =>
                      updateField(i, { type: e.target.value as FieldType })
                    }
                  >
                    <option value="text">Text</option>
                    <option value="email">Email</option>
                    <option value="tel">Phone</option>
                    <option value="textarea">Textarea</option>
                    <option value="file">File Upload</option>
                  </select>
                </Labeled>
                <div className="flex items-end gap-4">
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={f.required ?? false}
                      onChange={(e) => updateField(i, { required: e.target.checked })}
                    />
                    Required
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={f.visible ?? true}
                      onChange={(e) => updateField(i, { visible: e.target.checked })}
                    />
                    Visible
                  </label>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-end">
        <button
          onClick={save}
          disabled={saving}
          className="px-5 py-2 rounded-lg bg-brand hover:bg-brand-hover text-primary-foreground font-semibold disabled:opacity-60"
        >
          {saving ? "Saving…" : "Save Form"}
        </button>
      </div>
    </div>
  );
}

function Labeled({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block space-y-1">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}

function Inbox() {
  const [rows, setRows] = useState<Sub[]>([]);
  const [filter, setFilter] = useState<"all" | "new" | "read" | "archived">("all");
  const [active, setActive] = useState<Sub | null>(null);
  const [forwardTo, setForwardTo] = useState("");
  const [forwardNote, setForwardNote] = useState("");
  const [forwarding, setForwarding] = useState(false);
  const forwardFn = useServerFn(forwardFormSubmission);


  const load = () =>
    sb
      .from("form_submissions")
      .select("*")
      .order("created_at", { ascending: false })
      .then(({ data }: { data: Sub[] | null }) => setRows(data ?? []));

  useEffect(() => {
    load();
  }, []);

  const setStatus = async (id: string, status: Sub["status"]) => {
    const { error } = await sb.from("form_submissions").update({ status }).eq("id", id);
    if (error) return toast.error(error.message);
    load();
    if (active?.id === id) setActive({ ...active, status });
  };

  const list = rows.filter((r) => (filter === "all" ? true : r.status === filter));

  return (
    <div className="space-y-4">
      <div className="flex gap-2 flex-wrap">
        {(["all", "new", "read", "archived"] as const).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-3 py-1 rounded text-sm capitalize ${
              filter === f
                ? "bg-brand text-primary-foreground"
                : "bg-card border border-border"
            }`}
          >
            {f}
          </button>
        ))}
      </div>

      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-muted-foreground text-xs">
              <th className="text-left p-3">When</th>
              <th className="text-left">Name</th>
              <th className="text-left">Email</th>
              <th className="text-left">Status</th>
            </tr>
          </thead>
          <tbody>
            {list.map((r) => (
              <tr
                key={r.id}
                onClick={() => {
                  setActive(r);
                  if (r.status === "new") setStatus(r.id, "read");
                }}
                className="border-t border-border cursor-pointer hover:bg-background"
              >
                <td className="p-3 text-xs text-muted-foreground">
                  {new Date(r.created_at).toLocaleString()}
                </td>
                <td className="p-3 font-medium">{r.name ?? "—"}</td>
                <td className="p-3 text-muted-foreground">{r.email ?? "—"}</td>
                <td className="p-3">
                  <span
                    className={`px-2 py-0.5 rounded text-xs ${
                      r.status === "new"
                        ? "bg-brand/15 text-brand"
                        : r.status === "archived"
                        ? "bg-muted text-muted-foreground"
                        : "bg-background border border-border"
                    }`}
                  >
                    {r.status}
                  </span>
                </td>
              </tr>
            ))}
            {list.length === 0 && (
              <tr>
                <td colSpan={4} className="p-6 text-center text-muted-foreground">
                  No submissions.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {active && (
        <div
          className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
          onClick={() => setActive(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-card border border-border rounded-2xl p-6 max-w-lg w-full space-y-3 max-h-[90vh] overflow-auto"
          >
            <h2 className="font-display text-xl font-bold text-ink">
              {active.name ?? "Submission"}
            </h2>
            <p className="text-xs text-muted-foreground">
              {new Date(active.created_at).toLocaleString()}
            </p>
            <div className="text-sm space-y-1">
              <p><strong>Phone:</strong> {active.phone || "—"}</p>
              <p><strong>Email:</strong> {active.email || "—"}</p>
              {active.project_type && (
                <p><strong>Project Type:</strong> {active.project_type}</p>
              )}
            </div>
            <p className="text-sm whitespace-pre-wrap bg-background border border-border rounded-lg p-3">
              {active.message || "(no message)"}
            </p>
            {active.metadata && Object.keys(active.metadata).length > 0 && (
              <div className="text-xs space-y-1 bg-background border border-border rounded-lg p-3">
                <p className="font-semibold text-muted-foreground">Additional Fields</p>
                {Object.entries(active.metadata).map(([k, v]) => (
                  <p key={k}>
                    <strong>{k}:</strong> {String(v)}
                  </p>
                ))}
              </div>
            )}
            <div className="space-y-2 pt-2 border-t border-border">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                Forward submission
              </p>
              <div className="flex gap-2 flex-wrap">
                <input
                  type="email"
                  placeholder="recipient@example.com"
                  value={forwardTo}
                  onChange={(e) => setForwardTo(e.target.value)}
                  className="flex-1 min-w-[200px] px-3 py-2 rounded-lg bg-background border border-border text-sm"
                />
                <button
                  disabled={forwarding || !forwardTo}
                  onClick={async () => {
                    setForwarding(true);
                    try {
                      await forwardFn({
                        data: {
                          submissionId: active.id,
                          toEmail: forwardTo,
                          note: forwardNote || undefined,
                        },
                      });
                      toast.success(`Forwarded to ${forwardTo}`);
                      setForwardTo("");
                      setForwardNote("");
                    } catch (err) {
                      toast.error(err instanceof Error ? err.message : "Failed to forward");
                    } finally {
                      setForwarding(false);
                    }
                  }}
                  className="flex items-center gap-1 px-4 py-2 rounded-lg bg-brand hover:bg-brand-hover text-primary-foreground text-sm font-semibold disabled:opacity-60"
                >
                  <Forward size={14} />
                  {forwarding ? "Sending…" : "Forward"}
                </button>
              </div>
              <textarea
                placeholder="Optional note to include…"
                value={forwardNote}
                onChange={(e) => setForwardNote(e.target.value)}
                rows={2}
                className="w-full px-3 py-2 rounded-lg bg-background border border-border text-sm"
              />
            </div>

            <div className="flex gap-2 pt-2">
              <button
                onClick={() => setStatus(active.id, "read")}
                className="flex-1 py-2 rounded-lg bg-background border border-border text-sm"
              >
                Mark Read
              </button>
              <button
                onClick={() => setStatus(active.id, "archived")}
                className="flex-1 py-2 rounded-lg bg-background border border-border text-sm"
              >
                Archive
              </button>
              <button
                onClick={() => setActive(null)}
                className="py-2 px-4 rounded-lg bg-brand hover:bg-brand-hover text-primary-foreground text-sm font-semibold"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
